import { Input, Label, TextArea } from "@monsterindia/global-ui-components";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { API_ENDPOINT } from "../../../constants/appConstant";
import {
  CurrentlyWorkingCheckbox,
  EmploymentMonthDropdownOptionsList,
  EmploymentTypeList, // HideSalaryCheckbox,
  // HideSalaryNoticeCheckbox,
  NoticePeriodDropdownOptionsList,
  SalaryModeOptions,
} from "../../../constants/employment";
import { getNoticePeriodFromDays } from "../../../helpers/getNoticePeriod";
import { getSalaryBasedOnSiteContext } from "../../../helpers/getSalaryBasedOnSiteContext";
import { getYearsList } from "../../../helpers/getYearsList";
import { useNetwork } from "../../../hooks/useNetwork";
import {
  CheckboxOptionType,
  DropdownOptionType,
  MasterDataType,
} from "../../../types";
import { getSiteProps } from "../../../utils/siteProps";
import {
  endMonthValidation,
  endYearValidation,
} from "../../../validations/endDateValidation";
import {
  CheckboxController,
  DateController,
  MultiDropDownController,
  SalaryInputController,
  SingleDropDownController,
} from "../../controllers";
import { ActionButtonContainer } from "../common/ActionButtonContainer";
import { Heading } from "../common/Heading";
import { LinkedInConsent } from "../common/Linkedin/LinkedinConsent";
import { LinkedinURL } from "../common/Linkedin/LinkedinURL";
import { EmploymentUIProps } from "./Employment.types";
import { layoutConfig } from "./layoutConfig";

export const EmploymentApopUI = ({
  prefilledData,
  closeApop,
  flowType,
  backBtnClickHandler,
  onSubmit,
  isSubmitAPILoading,
  formRef,
}: EmploymentUIProps) => {
  const [companyData, setCompanyData] = useState<CheckboxOptionType[] | null>(
    null,
  );
  const [desingationData, setDesingationData] = useState<
    CheckboxOptionType[] | null
  >(null);

  const [industriesData, setIndustriesData] = useState<
    CheckboxOptionType[] | null
  >(null);
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    setError,
    getValues,
    clearErrors,
  } = useForm({
    mode: "onChange",
  });

  const [isAutoUpdateChecked, setIsAutoUpdateChecked] = useState(false);
  const [showLinkedinURL, setShowLinkedinURL] = useState(true);
  // const [showLinkedinConsent, setShowLinkedinConsent] = useState(true);

  const [isCurrentlyWorking, setIsCurrentlyWorking] = useState(false);
  const [isServingNotice, setIsServingNotice] = useState(false);

  const [startMonthDisabled, setStartMonthDisabled] = useState(true);
  const [endMonthDisabled, setEndMonthDisabled] = useState(true);
  const [endYearDisabled, setEndYearDisabled] = useState(true);
  const [manualCurrentJobTitle, setManualCurrentJobTitle] = useState(false);
  const [manualCompany, setManualCompany] = useState(false);
  const [manualOfferedJobTitle, setManualOfferedJobTitle] = useState(false);
  const [manualNewCompany, setManualNewCompany] = useState(false);
  const [isDropdownOptionLoading, setIsDropdownOptionLoading] = useState(false);

  // DEFAULT CURRENCY based on the site configurations
  const siteConfig = getSiteProps(window.location.hostname);
  const defaultCurrencySelected = {
    value: siteConfig?.currency_name,
    id: siteConfig?.currency_id,
    text: siteConfig?.currency_code,
  };

  const salaryMode =
    siteConfig?.site_map === "rexmonster"
      ? SalaryModeOptions[1]
      : SalaryModeOptions[0];

  const currentYears = new Date().getFullYear();

  const endYearListData = useMemo(() => {
    return getYearsList();
  }, [currentYears]);

  const [endYearList, setEndYearList] = useState<DropdownOptionType[]>([]);
  const [endMonthList, setEndMonthList] = useState<DropdownOptionType[]>([]);

  const { fetchData: fetchAPI } = useNetwork();

  const updatedSalary = getSalaryBasedOnSiteContext({
    absoluteSalary: (prefilledData?.employmentPrefill as any)?.salary
      ?.absoluteValue
      ? ((prefilledData?.employmentPrefill as any)?.salary
          ?.absoluteValue as number)
      : 0,
    siteContext: siteConfig?.site_map ?? "rexmonster",
    salaryMode: (prefilledData?.employmentPrefill as any)?.salary?.salaryMode
      ?.id
      ? (prefilledData?.employmentPrefill as any)?.salary?.salaryMode
      : salaryMode,
  });

  const prefillDataHelper = (prefillData: any) => {
    const { employmentPrefill, linkedInResponse } = prefillData;

    if (employmentPrefill) {
      const {
        designation,
        company,
        industries,
        startDate,
        endDate,
        salary,
        noticePeriod,
        offeredSalary,
        offeredDesignation,
        newCompany,
        employmentType,
        title,
      } = employmentPrefill;
      if (designation && Object.keys(designation).length > 0) {
        const { id, text } = designation;
        if (id) {
          setValue("jobTitle", {
            value: text,
            id,
            text,
          });
        } else {
          setValue("jobTitle", {
            value: "Others",
            text: "Others",
          });
          setManualCurrentJobTitle(true);
          setValue("manualCurrentJobTitle", text);
        }
      }
      if (company && Object.keys(company).length > 0) {
        const { id, text } = company;
        if (id) {
          setValue("company", {
            value: text,
            id,
            text,
          });
          setManualCompany(false);
        } else {
          setValue("company", {
            value: "Other",
            text: "Other",
          });
          setManualCompany(true);
          setValue("manualCompany", text);
        }
      }

      if (industries && Object.keys(industries).length > 0) {
        const updatedIndustries = industries.map((industry: any) => {
          const { id, text } = industry;
          return {
            id,
            text,
            value: text,
          };
        });
        setValue("industries", updatedIndustries);
      }

      if (startDate) {
        const startYearPrefill = startDate.split("-")[0];
        const startMonthPrefill = startDate.split("-")[1];

        const startYear = endYearListData.filter(
          (year) => year.text === startYearPrefill,
        );

        if (startYear.length > 0) {
          setValue("startYear", {
            id: startYear[0].id,
            text: startYear[0].text,
          });
        }

        const startMonth = EmploymentMonthDropdownOptionsList.filter(
          (month) => month.id === parseInt(startMonthPrefill),
        );

        if (startMonth.length > 0) {
          setValue("startMonth", {
            id: startMonth[0].id,
            text: startMonth[0].text,
          });
        }
      }
      if (endDate) {
        setIsCurrentlyWorking(false);
        const endYearPrefill = endDate.split("-")[0];
        const endMonthPrefill = endDate.split("-")[1];

        const endYear = endYearListData.filter(
          (year) => year.text === endYearPrefill,
        );

        if (endYear.length > 0) {
          setValue("endYear", {
            id: endYear[0].id,
            text: endYear[0].text,
          });
          setEndYearDisabled(false);
        }

        const endMonth = EmploymentMonthDropdownOptionsList.filter(
          (month) => month.id === parseInt(endMonthPrefill),
        );

        if (endMonth.length > 0) {
          setValue("endMonth", {
            id: endMonth[0].id,
            text: endMonth[0].text,
          });

          setEndMonthDisabled(false);
          const newMonthList = endMonthValidation(
            getValues("startYear"),
            getValues("startMonth"),
            getValues("endYear"),
            EmploymentMonthDropdownOptionsList,
          ) as DropdownOptionType[];
          setEndMonthList(newMonthList);
        }
      }
      if (salary?.absoluteValue) {
        setValue("salary", updatedSalary.toString());
      }
      if (salary?.currency) {
        setValue("currency", {
          value: salary.currency,
          id: salary.currency,
          text: salary.currency,
        });
        // if (salary?.confidential && salary.confidential !== null) {
        //   setValue("hideSalary", salary.confidential);
        // }
        if (offeredSalary) {
          const { absoluteValue, currency, confidential } = offeredSalary;
          if (absoluteValue) {
            setValue("newSalary", absoluteValue.toString());
          }

          setValue("newOfferedCurrency", {
            value: currency,
            id: currency,
            text: currency,
          });
          // setValue("hidePotentialSalary", confidential);
        }

        if (offeredDesignation && Object.keys(offeredDesignation).length > 0) {
          const { id, text } = offeredDesignation;
          if (id) {
            setValue("offeredJobTitle", {
              value: text,
              id,
              text,
            });
          } else {
            setValue("offeredJobTitle", {
              value: "Others",
              text: "Others",
            });
            setManualOfferedJobTitle(true);
            setValue("manualOfferedJobTitle", text);
          }
        }

        if (newCompany && Object.keys(newCompany).length > 0) {
          const { id, text } = newCompany;
          if (id) {
            setValue("newCompany", {
              value: text,
              id,
              text,
            });
          } else {
            setValue("newCompany", {
              value: "Other",
              text: "Other",
            });
            setManualNewCompany(true);
            setValue("manualNewCompany", text);
          }
        }
      }
      if (noticePeriod && Object.keys(noticePeriod).length && !endDate) {
        const { serving, lastWorkingDay, days } = noticePeriod;
        if (serving) {
          setIsServingNotice(true);
          if (lastWorkingDay) {
            setValue("date", lastWorkingDay);
          }
        } else {
          setIsServingNotice(false);
        }

        setValue("notice", {
          value: days,
          text: NoticePeriodDropdownOptionsList.find((notice) => {
            const noticePeriodInText = getNoticePeriodFromDays(days, serving);
            return notice.text === noticePeriodInText;
          })?.text,
        });
      }
      if (employmentType) {
        setValue("employmentType", {
          text: employmentType.text,
        });
      }
      if (title) {
        setValue("title", title);
      }
    }

    if (linkedInResponse) {
      const { linkedInConsent, url } = linkedInResponse;

      if (url) {
        setValue("linkedin", url);
        setShowLinkedinURL(false);
      } else {
        setShowLinkedinURL(true);
      }
      // if (linkedInConsent === false || linkedInConsent === true) {
      //   setValue("isToggled", linkedInConsent);
      //   setShowLinkedinConsent(false);
      // } else {
      //   setShowLinkedinConsent(true);
      //   setValue("isToggled", true);
      //   setIsAutoUpdateChecked(true);
      // }
    } else {
      setShowLinkedinURL(true);
      // setShowLinkedinConsent(true);
      // setValue("isToggled", true);
      // setIsAutoUpdateChecked(true);
    }
  };

  useEffect(() => {
    if (prefilledData) {
      prefillDataHelper(prefilledData);
    }
  }, [prefilledData]);

  const companyDropdownOptionsHandler = async (company: string) => {
    setIsDropdownOptionLoading(true);
    const companyDataResponse = await fetchAPI(
      `${API_ENDPOINT().seekerProfile.masterDataMiddleware}?type=COMPANY&name=${company}`,
      "GET",
    );

    const masterCompanyData = (companyDataResponse.data as any)
      ?.data as MasterDataType[];

    const updatedCompanyData =
      masterCompanyData && masterCompanyData.length > 0
        ? masterCompanyData.map((company) => {
            return {
              value: company.name,
              id: company.uuid,
              text: company.name,
            };
          })
        : null;

    setIsDropdownOptionLoading(false);
    setCompanyData(updatedCompanyData);
  };

  const designationDropdownOptionsHandler = async (desingation: string) => {
    setIsDropdownOptionLoading(true);
    const res = await fetchAPI(
      `${API_ENDPOINT().seekerProfile.masterDataMiddleware}?type=DESIGNATION&name=${desingation}`,
      "GET",
    );

    const masterDesingationData = (res.data as any)?.data as MasterDataType[];

    const updatedDesingationData =
      masterDesingationData && masterDesingationData.length > 0
        ? masterDesingationData.map((desingation) => {
            return {
              value: desingation.name,
              id: desingation.uuid,
              text: desingation.name,
            };
          })
        : null;
    setIsDropdownOptionLoading(false);
    setDesingationData(updatedDesingationData);
  };
  const industryDropdownOptionsHandler = async (industry: string) => {
    setIsDropdownOptionLoading(true);
    const res = await fetchAPI(
      `${API_ENDPOINT().seekerProfile.masterDataMiddleware}?type=INDUSTRY&name=${industry}`,
      "GET",
    );

    const masterIndustryData = (res.data as any)?.data as MasterDataType[];

    const updatedIndustryData =
      masterIndustryData && masterIndustryData.length > 0
        ? masterIndustryData.map((indus) => {
            return {
              value: indus.name,
              id: indus.uuid,
              text: indus.name,
            };
          })
        : null;
    setIsDropdownOptionLoading(false);
    setIndustriesData(updatedIndustryData);
  };

  useEffect(() => {
    // scroll to top on page load

    if (layout.isScrollToTop) {
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    }

    if (!(prefilledData?.employmentPrefill as any)?.endDate) {
      setIsCurrentlyWorking(true);
    }
    // set default currency value for salary and offered salary currency fields in the rect-hook-form
    setValue("currency", getValues("currency") ?? defaultCurrencySelected);
    setValue(
      "newOfferedCurrency",
      getValues("newOfferedCurrency") ?? defaultCurrencySelected,
    );
  }, []);

  const todaysDate = new Date().toISOString().split("T")[0];
  const monthDataHandler = useMemo(() => {
    const currentYear = todaysDate.split("-")[0];

    // If the start year is the current year, then we need to disable the future months
    if (getValues("startYear") && getValues("startYear").text === currentYear) {
      const currentMonth = parseInt(todaysDate.split("-")[1]);
      const months = EmploymentMonthDropdownOptionsList.filter((month) => {
        const tempMonth = month.id as number;
        return tempMonth <= currentMonth;
      });

      return months;
    }

    return EmploymentMonthDropdownOptionsList;
  }, [todaysDate, getValues("startYear")]);

  const handleStartMonthDisabled = () => {
    const value = getValues("startYear");
    if (value && getValues("startMonth") && !isCurrentlyWorking) {
      const newYearList = endYearValidation(
        value,
        getValues("startMonth"),
        endYearListData,
      ) as DropdownOptionType[];
      setEndYearList(newYearList);
    }
    if (value && startMonthDisabled) {
      setStartMonthDisabled(false);
    }
  };

  const handleEndMonthDisabled = () => {
    // For months
    if (
      getValues("startYear") &&
      getValues("startMonth") &&
      getValues("endYear") &&
      !isCurrentlyWorking
    ) {
      const newMonthList = endMonthValidation(
        getValues("startYear"),
        getValues("startMonth"),
        getValues("endYear"),
        EmploymentMonthDropdownOptionsList,
      ) as DropdownOptionType[];
      setEndMonthList(newMonthList);
      const value = getValues("endYear");
      if (value !== undefined && endMonthDisabled) {
        setEndMonthDisabled(false);
      }
    }
  };

  const handleEndYearDisabled = () => {
    const value = getValues("startMonth");
    if (getValues("startYear") && value) {
      const newYearList = endYearValidation(
        getValues("startYear"),
        getValues("startMonth"),
        endYearListData,
      ) as DropdownOptionType[];
      setEndYearList(newYearList);
    }
    if (value !== undefined && endYearDisabled) {
      setEndYearDisabled(false);
    }
  };

  useEffect(() => {
    if (isCurrentlyWorking) {
      setValue("endYear", undefined);
      setValue("endMonth", undefined);
      setEndMonthDisabled(true);
      setEndYearDisabled(false);
    } else {
      handleStartMonthDisabled();
    }
  }, [isCurrentlyWorking]);

  const onSubmitWrapper = (data: any) => {
    onSubmit(
      data,
      salaryMode,
      isCurrentlyWorking,
      isAutoUpdateChecked,
      siteConfig?.site_map === "rexmonster",
    );
  };

  const layout = useMemo(() => {
    return layoutConfig(flowType);
  }, [flowType]);

  return (
    <div className={layout.outerClassName}>
      {layout.showHeading && (
        <Heading
          className={layout.showHeading.className}
          headingText={layout.showHeading.text}
          isMobile={window.innerWidth < 768}
          onCloseBtnClickHandler={() => {
            closeApop();
          }}
          onBackBtnClickHandler={
            window.innerWidth < 768 ? undefined : backBtnClickHandler
          }
        />
      )}

      <form
        ref={formRef}
        className={`l1-relative l1-flex l1-w-full l1-flex-col l1-gap-4 ${layout.formClassName}`}
        id="employmentDetailsForm"
        onSubmit={handleSubmit(onSubmitWrapper)}
        noValidate
      >
        {layout.isShowLinkedInUrl && showLinkedinURL && (
          <>
            <p className="l1-text-xs l1-text-gray-400">
              This is crucial for a recruiter to understand!
            </p>
            <LinkedinURL
              register={register}
              setValue={setValue}
              errors={errors}
              setError={setError}
              clearError={() => clearErrors("linkedin")}
            />
          </>
        )}
        <Label
          labelText={layout.jobTitle ?? "Current Job Title"}
          isRequired="true"
          className="!l1-text-sm"
        >
          <SingleDropDownController
            name="jobTitle"
            control={control}
            isRequired={true}
            placeholder="Search job title"
            isSearchable={true}
            options={desingationData ?? []}
            isLoading={isDropdownOptionLoading}
            onChangeHandler={(inputText: string) => {
              if (inputText && !inputText.includes("Other")) {
                setValue("manualCurrentJobTitle", inputText);
              }
              designationDropdownOptionsHandler(inputText);
            }}
            onSelect={(value: DropdownOptionType) => {
              clearErrors("jobTitle");
              setValue("jobTitle", value);
              if (value && value.text === "Others") {
                setManualCurrentJobTitle(true);
              } else {
                setManualCurrentJobTitle(false);
              }
            }}
            errorMessage={errors.jobTitle ? "This field is required" : ""}
          />
        </Label>
        {manualCurrentJobTitle && (
          <Label
            labelText="Enter Your Job Title"
            isRequired={true}
            className="!l1-text-sm"
          >
            <Input
              id="manualCurrentJobTitle"
              placeholder="Enter Your Job Title"
              {...register("manualCurrentJobTitle", {
                required: true,
                minLength: {
                  value: 2,
                  message: "Please enter a valid job title",
                },
                maxLength: {
                  value: 100,
                  message: "You have reached maximum limit of 100",
                },
              })}
              errorText={
                errors.manualCurrentJobTitle
                  ? errors.manualCurrentJobTitle.message ||
                    "This field is required"
                  : ""
              }
            />
          </Label>
        )}
        <Label labelText="Company" isRequired="true" className="!l1-text-sm">
          <SingleDropDownController
            name="company"
            control={control}
            isRequired={true}
            placeholder="Enter your company name"
            isSearchable={true}
            options={companyData ?? []}
            isLoading={isDropdownOptionLoading}
            onChangeHandler={(inputText: string) => {
              if (inputText && !inputText.includes("Other")) {
                setValue("manualCompany", inputText);
              }
              companyDropdownOptionsHandler(inputText);
            }}
            onSelect={(value: DropdownOptionType) => {
              clearErrors("company");
              setValue("company", value);
              if (value && value.text === "Other") {
                setManualCompany(true);
              } else {
                setManualCompany(false);
              }
            }}
            errorMessage={errors.company ? "This field is required" : ""}
          />
        </Label>
        {manualCompany && (
          <Label
            labelText="Enter Your Company"
            isRequired={true}
            className="!l1-text-sm"
          >
            <Input
              id="manualCompany"
              placeholder="Enter Your Company"
              {...register("manualCompany", {
                required: true,
                minLength: {
                  value: 2,
                  message: "Please enter a valid company name",
                },
                maxLength: {
                  value: 100,
                  message: "You have reached maximum limit of 100",
                },
              })}
              errorText={
                errors.manualCompany
                  ? errors.manualCompany.message || "This field is required"
                  : ""
              }
            />
          </Label>
        )}
        <Label labelText="Start Date" isRequired={true} className="!l1-text-sm">
          <div className="l1-flex l1-flex-row l1-gap-2">
            <SingleDropDownController
              name="startYear"
              control={control}
              isRequired={true}
              placeholder="Years"
              isSearchable={false}
              options={endYearListData}
              onSelect={(value: DropdownOptionType) => {
                clearErrors("startYear");
                setValue("startYear", value);
                handleStartMonthDisabled();
                setValue("endYear", undefined);
                setValue("endMonth", undefined);
                setEndMonthDisabled(true);
              }}
              errorMessage={errors.startYear ? "This field is required" : ""}
            />
            <SingleDropDownController
              name="startMonth"
              control={control}
              isRequired={true}
              placeholder="Months"
              isSearchable={false}
              isDisabled={startMonthDisabled}
              options={monthDataHandler}
              onSelect={(value: DropdownOptionType) => {
                clearErrors("startMonth");
                setValue("startMonth", value);
                handleEndYearDisabled();
                setValue("endYear", undefined);
                setValue("endMonth", undefined);
                setEndMonthDisabled(true);
              }}
              errorMessage={
                !startMonthDisabled
                  ? errors.startMonth
                    ? "This field is required"
                    : ""
                  : undefined
              }
            />
          </div>
        </Label>
        {!isCurrentlyWorking ? (
          <Label labelText="End Date" isRequired={true} className="!l1-text-sm">
            <div className="flex flex-row gap-2">
              <SingleDropDownController
                name="endYear"
                control={control}
                isRequired={true}
                placeholder="Years"
                isSearchable={false}
                isDisabled={endYearDisabled}
                options={endYearList ?? []}
                onSelect={(value: DropdownOptionType) => {
                  clearErrors("endYear");
                  setValue("endYear", value);
                  handleEndMonthDisabled();
                  setValue("endMonth", undefined);
                }}
                errorMessage={
                  !endYearDisabled
                    ? errors.endYear && "This field is required"
                    : undefined
                }
              />
              <SingleDropDownController
                name="endMonth"
                control={control}
                isRequired={true}
                placeholder="Months"
                isSearchable={false}
                isDisabled={endMonthDisabled}
                options={endMonthList}
                onSelect={(value: DropdownOptionType) => {
                  clearErrors("endMonth");
                  setValue("endMonth", value);
                }}
                errorMessage={
                  !endMonthDisabled
                    ? errors.endMonth && "This field is required"
                    : undefined
                }
              />
            </div>
          </Label>
        ) : null}
        <CheckboxController
          control={control}
          name="isCurrentlyWorking"
          options={CurrentlyWorkingCheckbox}
          isDefaultChecked={
            (prefilledData?.employmentPrefill as any)?.endDate ? false : true
          }
          onChange={(isChecked) => {
            if (isChecked) {
              setIsCurrentlyWorking(true);
            } else {
              setIsCurrentlyWorking(false);
            }
          }}
        />
        {/* check if experienced then also we will hide 
            salary but only if isCheckExperienceLevel is true 
        */}
        {(layout.isShowCurrentSalary ||
          !(layout.isCheckExperienceLevel
            ? prefilledData?.experienceLevel &&
              prefilledData?.experienceLevel === "EXPERIENCED"
            : true)) && (
          <>
            <Label
              labelText={`Current Salary (${salaryMode.name})`}
              isRequired={true}
              className="!l1-text-sm"
            >
              <SalaryInputController
                name="salary"
                control={control}
                isRequired={true}
                placeholder={
                  siteConfig?.site_map === "rexmonster"
                    ? "Enter Salary"
                    : "Enter Salary (monthly)"
                }
                currency={getValues("currency") ?? defaultCurrencySelected}
                setCurrency={(value: unknown) => {
                  setValue("currency", value);
                }}
                absoluteSalary={
                  getValues("salary")
                    ? getValues("salary")
                    : (prefilledData?.employmentPrefill as any)?.salary
                          ?.absoluteValue &&
                        (
                          prefilledData?.employmentPrefill as any
                        )?.salary?.absoluteValue.toString()
                      ? updatedSalary.toString()
                      : ""
                }
                setAbsoluteSalary={(value: string) => {
                  if (value === "0") {
                    setError(
                      "salary",
                      {
                        type: "",
                        message: "Salary value cannot be 0",
                      },
                      { shouldFocus: true },
                    );
                  } else {
                    clearErrors("salary");
                  }
                  setValue("salary", value);
                }}
                isSalaryAnnual={siteConfig?.site_map === "rexmonster"}
                errorMessage={
                  errors.salary
                    ? errors.salary?.message
                      ? errors.salary.message.toString()
                      : "This field is required"
                    : ""
                }
                tooltipText={
                  siteConfig?.site_map === "rexmonster"
                    ? "To rank high in recruiter searches, ensure you enter the correct annual salary."
                    : undefined
                }
              />
            </Label>
          </>
        )}
        {isCurrentlyWorking && (
          <>
            <Label
              labelText="Notice Period"
              isRequired={true}
              className="!l1-text-sm"
            >
              <SingleDropDownController
                name="notice"
                control={control}
                isRequired={true}
                placeholder="Select Notice Period"
                isSearchable={false}
                options={NoticePeriodDropdownOptionsList}
                onSelect={(value: DropdownOptionType) => {
                  if (value.text === "Serving Notice Period") {
                    setIsServingNotice(true);
                  } else {
                    setIsServingNotice(false);
                  }
                  setValue("notice", value);
                  clearErrors("notice");
                }}
                errorMessage={errors.notice ? "This field is required" : ""}
              />
            </Label>

            {isServingNotice && (
              <div className="l1-flex l1-flex-col l1-gap-4">
                <Label
                  labelText="Last Working Day"
                  isRequired={true}
                  className="!l1-text-sm"
                >
                  <DateController
                    name="date"
                    control={control}
                    isRequired={true}
                    placeholder="Select Last Working Date"
                    onDateSelect={(date: string) => {
                      setValue("date", date);
                      clearErrors("date");
                    }}
                    minDate={todaysDate}
                    errorMessage={errors.date ? "This field is required" : ""}
                  />
                </Label>

                {/* FUSE-12380 Hide offered - company, salary and job title */}
                {/* {(layout.isShowServingFields ||
                  !(layout.isCheckExperienceLevel
                    ? prefilledData?.experienceLevel &&
                      prefilledData?.experienceLevel === "EXPERIENCED"
                    : true)) && (
                  <>
                    <Label
                      labelText={`New Offered Salary (${salaryMode.name})`}
                      isRequired={false}
                      className="!l1-text-sm"
                    >
                      <SalaryInputController
                        name="newSalary"
                        control={control}
                        currency={
                          getValues("newOfferedCurrency") ??
                          defaultCurrencySelected
                        }
                        setCurrency={(value: unknown) => {
                          setValue("newOfferedCurrency", value);
                        }}
                        absoluteSalary={getValues("newSalary") ?? ""}
                        setAbsoluteSalary={(value: string) => {
                          if (value === "0") {
                            setError("newSalary", {
                              type: "validate",
                              message: "Salary value cannot be 0",
                            });
                          } else {
                            clearErrors("newSalary");
                          }
                          setValue("newSalary", value);
                        }}
                        isRequired={true}
                        placeholder={
                          siteConfig?.site_map === "rexmonster"
                            ? "Enter Salary"
                            : "Enter Salary (monthly)"
                        }
                        errorMessage={
                          errors.newSalary
                            ? errors.newSalary?.message
                              ? errors.newSalary.message.toString()
                              : "This field is required"
                            : ""
                        }
                        tooltipText={
                          siteConfig?.site_map === "rexmonster"
                            ? "To rank high in recruiter searches, ensure you enter the correct annual salary."
                            : undefined
                        }
                      />
                    </Label>

                    <Label
                      labelText="Offered Job Title"
                      isRequired={false}
                      className="!l1-text-sm"
                    >
                      <SingleDropDownController
                        name="offeredJobTitle"
                        control={control}
                        isRequired={false}
                        placeholder="Offered job title"
                        isSearchable={true}
                        options={desingationData ?? []}
                        onChangeHandler={(inputText: string) => {
                          if (inputText && !inputText.includes("Other")) {
                            setValue("manualOfferedJobTitle", inputText);
                          }
                          designationDropdownOptionsHandler(inputText);
                        }}
                        onSelect={(value: DropdownOptionType) => {
                          setValue("offeredJobTitle", value);
                          if (value && value.text === "Others") {
                            setManualOfferedJobTitle(true);
                          } else {
                            setManualOfferedJobTitle(false);
                          }
                        }}
                        errorMessage={
                          errors.offeredJobTitle ? "This field is required" : ""
                        }
                      />
                    </Label>

                    {manualOfferedJobTitle && (
                      <Label
                        labelText="Enter Your Offered Job Title"
                        isRequired={true}
                        className="!l1-text-sm"
                      >
                        <Input
                          id="manualOfferedJobTitle"
                          placeholder="Enter Your Offered Job Title"
                          {...register("manualOfferedJobTitle", {
                            required: true,
                          })}
                          errorText={
                            errors.manualOfferedJobTitle
                              ? "This field is required"
                              : ""
                          }
                        />
                      </Label>
                    )}

                    <Label
                      labelText="New Company"
                      isRequired={false}
                      className="!l1-text-sm"
                    >
                      <SingleDropDownController
                        name="newCompany"
                        control={control}
                        isRequired={false}
                        placeholder="New company name"
                        isSearchable={true}
                        options={companyData ?? []}
                        onChangeHandler={(inputText: string) => {
                          if (inputText && !inputText.includes("Other")) {
                            setValue("manualNewCompany", inputText);
                          }
                          companyDropdownOptionsHandler(inputText);
                        }}
                        onSelect={(value: DropdownOptionType) => {
                          setValue("newCompany", value);
                          if (value && value.text === "Other") {
                            setManualNewCompany(true);
                          } else {
                            setManualNewCompany(false);
                          }
                        }}
                        errorMessage={
                          errors.newCompany ? "This field is required" : ""
                        }
                      />
                    </Label>

                    {manualNewCompany && (
                      <Label
                        labelText="Enter Your New Company"
                        isRequired={true}
                        className="!l1-text-sm"
                      >
                        <Input
                          id="manualNewCompany"
                          placeholder="Enter Your New Company"
                          {...register("manualNewCompany", {
                            required: true,
                          })}
                          errorText={
                            errors.manualNewCompany
                              ? "This field is required"
                              : ""
                          }
                        />
                      </Label>
                    )}
                  </>
                )} */}
              </div>
            )}
          </>
        )}
        <Label labelText="Industry" isRequired={false} className="!l1-text-sm">
          <MultiDropDownController
            name="industries"
            maxSelect={2}
            control={control}
            isRequired={false}
            isSearchable={true}
            options={industriesData ?? []}
            isLoading={isDropdownOptionLoading}
            onSelect={(value: DropdownOptionType) => {
              clearErrors("industries");
              setValue("industries", value);
            }}
            onChangeHandler={(inputText: string) => {
              industryDropdownOptionsHandler(inputText);
            }}
            placeholder={
              getValues("industries") && getValues("industries").length >= 2
                ? "Maximum 2 industries can be selected"
                : "Enter or select your Industry"
            }
            isDisabled={false}
          />
        </Label>
        {layout.showEmploymentType && (
          <Label
            labelText="Employment Type"
            className="!l1-text-sm"
            isRequired={false}
          >
            <SingleDropDownController
              name="employmentType"
              control={control}
              isRequired={false}
              placeholder="Select Employment Type"
              isSearchable={false}
              options={EmploymentTypeList}
              onSelect={(value: DropdownOptionType) => {
                setValue("employmentType", value);
              }}
            />
          </Label>
        )}
        {layout.isShowDescription && (
          <Label
            labelText="Description"
            isRequired={false}
            className="!l1-text-sm"
          >
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextArea
                  {...field}
                  id="title"
                  value={field.value ?? ""}
                  placeholder="Enter Your Description"
                  maxChars={4000}
                  className="!l1-h-32 !l1-resize-none"
                />
              )}
            />
          </Label>
        )}
        {/* {layout.isShowLinkedInConsent && showLinkedinConsent && (
          <LinkedInConsent
            checked={isAutoUpdateChecked}
            handleConsentToggle={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValue("isToggled", e.target.checked);
              setIsAutoUpdateChecked(e.target.checked);
            }}
          />
        )} */}
        {layout.showActionBtn &&
          (layout.showActionBtn === "compactAction" ? (
            <ActionButtonContainer
              primaryBtnLabel="Save"
              secondaryBtnLabel={window.innerWidth < 768 ? "Cancel" : undefined}
              secondaryBtnOnClick={
                window.innerWidth < 768 ? backBtnClickHandler : undefined
              }
              isLoading={isSubmitAPILoading}
            />
          ) : (
            <ActionButtonContainer
              primaryBtnLabel="Save"
              secondaryBtnLabel="Cancel"
              secondaryBtnOnClick={backBtnClickHandler}
              isLoading={isSubmitAPILoading}
            />
          ))}
      </form>
    </div>
  );
};
